import {
	contrastGrade,
	contrastRatio,
	hexToRgb,
	normalizeHex,
	parseColor,
	readableInk,
	rgbToCmyk,
	type BrandColorCategory,
	type BrandSwatch,
	type PaletteColorPantoneInput,
} from '@codezo/brand-detail';
import { Info, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { PantoneField } from './PantoneField';

/**
 * Add or edit one color in the brand palette.
 *
 * ── Why one modal for both ──
 *
 * The palette had no edit at all: a typo in a hex meant removing the color and
 * adding it back, which loses its place in the bucket. Add and edit ask for
 * exactly the same fields, so they are the same form — `swatch` decides which,
 * and whether the footer offers Remove.
 *
 * ── Why the readouts are here at all ──
 *
 * A brand color is handed to three different trades. RGB and the contrast
 * grades are for whoever puts it on a screen; CMYK is for whoever prints it.
 * All three used to be worked out elsewhere — in a design tool, or not at all —
 * and "is our secondary legible as body type" is a question this page should be
 * able to answer while the color is being picked, not after a client complains.
 *
 * CMYK is the naive conversion and says so — it is a sanity check, not a print
 * spec. The print spec is the Pantone reference below, which comes off the real
 * catalogue.
 *
 * ── Why there is no category control ──
 *
 * The palette still HAS four buckets — `primary`, `secondary`, `accent`,
 * `neutral` — and the backend reads `primary` for real work: it is what
 * `dominantColors` and the AI brief's "prioritize primary brand colors" come
 * from. What went away is asking a person to choose one, which in practice was
 * a field nobody had a confident answer for.
 *
 * So the bucket is decided here instead of in the form: a new colour lands in
 * `primary`, and an edited colour KEEPS the bucket it is already in. That last
 * part matters — a colour sitting in `secondary` would otherwise be silently
 * relocated the first time anyone fixed a typo in its name.
 */

const DEFAULT_HEX = '#6B7F5E';

export interface ColorEntry {
	name: string;
	hex: string;
	/** Free text, not a tag list — the backend narrowed this to a String and
	 * the rest of this file already reads and writes it as one. */
	usage: string;
	/** Which palette bucket the colour belongs in. Decided here rather than in
	 * the form — see the note above. */
	category: BrandColorCategory;
	/** The print spec, or null. Separate from `hex` on purpose — see
	 * `PantoneField`. */
	pantone: PaletteColorPantoneInput | null;
}

export function ColorModal({
	open,
	onOpenChange,
	/** The color being edited, or null to add a new one. */
	swatch,
	/** The rest of the palette — only so a duplicate hex can be pointed out. */
	palette,
	onSave,
	onRemove,
	loading,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	swatch?: BrandSwatch | null;
	palette: BrandSwatch[];
	onSave: (color: ColorEntry) => void;
	onRemove?: () => void;
	loading?: boolean;
}) {
	const editing = Boolean(swatch);

	const [hex, setHex] = useState(DEFAULT_HEX);
	/* The field is edited as free text — a half-typed "#6B7" is not a color yet,
	   so the committed hex and what is in the box are two different things. */
	const [draft, setDraft] = useState(DEFAULT_HEX.slice(1));
	const [name, setName] = useState('');
	const [usage, setUsage] = useState('');
	const [pantone, setPantone] = useState<PaletteColorPantoneInput | null>(null);
	/**
	 * Whether the hex is the user's choice or still the placeholder.
	 *
	 * `BrandColor.hex` is non-null, so a colour always HAS one — but a colour
	 * specified for print starts from a Pantone code, not a screen value. Until
	 * the user touches the picker or the field, the hex is an unclaimed default:
	 * the Pantone panel browses rather than matching against it, and picking a
	 * swatch adopts the book's own value as the screen colour.
	 */
	const [hexChosen, setHexChosen] = useState(false);
	const [touched, setTouched] = useState(false);

	/* Seeded on open rather than on mount: the dialog keeps its tree between
	   openings, so a component that seeded once would show the previous color. */
	useEffect(() => {
		if (!open) return;
		const initialHex = swatch ? normalizeHex(swatch.hex) : DEFAULT_HEX;
		setHex(initialHex);
		setDraft(initialHex.slice(1));
		setName(swatch?.name ?? '');
		setUsage(swatch?.usage ?? '');
		/* An existing colour's hex is already someone's decision. */
		setHexChosen(Boolean(swatch));
		setPantone(
			swatch?.pantone
				? {
						book: swatch.pantone.book,
						code: swatch.pantone.code,
						hex: swatch.pantone.hex ?? undefined,
					}
				: null
		);
		setTouched(false);
	}, [open, swatch]);

	const commit = (value: string, chosen = true) => {
		setHex(value);
		setDraft(value.slice(1));
		if (chosen) setHexChosen(true);
	};

	const onDraftChange = (value: string) => {
		const clean = value.replace(/^#/, '');
		setDraft(clean);
		setHexChosen(true);
		const parsed = parseColor(value);
		/* Commit while typing only once the value is whole, or the swatch
		   flickers through every prefix on the way to six digits. */
		if (parsed && (clean.length === 6 || clean.length === 3 || /rgb/i.test(value))) {
			setHex(parsed);
		}
	};

	const draftValid = Boolean(parseColor(draft));
	const nameError = touched && !name.trim() ? 'Give the color a name' : null;
	const canSave = draftValid && Boolean(name.trim());

	const [r, g, b] = hexToRgb(hex);
	const cmyk = rgbToCmyk([r, g, b]);
	const onWhite = contrastRatio(hex, '#FFFFFF');
	const onBlack = contrastRatio(hex, '#141414');
	const ink = readableInk(hex);

	/* The color's own entry is not a duplicate of itself. */
	const duplicate = palette.find(
		(p) =>
			normalizeHex(p.hex) === hex &&
			!(swatch && p.category === swatch.category && p.index === swatch.index)
	);

	const submit = () => {
		setTouched(true);
		if (!canSave || loading) return;
		onSave({
			name: name.trim(),
			hex,
			usage: usage.trim(),
			/* Not the user's choice any more — see the note at the top. */
			category: swatch?.category ?? 'primary',
			pantone,
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>{editing ? 'Edit color' : 'Add color'}</DialogTitle>
					<DialogDescription>
						{editing
							? 'Change the color, what it is called, or what it is for.'
							: 'Add a color to the brand palette.'}
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={submit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{/* Live preview — the color at a size you can actually
							    judge, wearing its own name and legible type. */}
							<div
								className="flex h-24 flex-col justify-between rounded-[var(--r-3)] border border-line-2 px-4 py-3.5 transition-colors duration-[var(--dur-fast)]"
								style={{ backgroundColor: hex, color: ink }}
							>
								<div className="flex items-baseline justify-between gap-3">
									<span className="truncate text-lg font-medium">
										{name.trim() || 'Untitled color'}
									</span>
									<span className="shrink-0 text-2xl font-medium tracking-tight">
										Aa
									</span>
								</div>
								<span className="truncate font-mono text-xs opacity-80">
									{hex}
									{pantone ? ` · PANTONE ${pantone.code}` : ''}
									{usage.trim() ? ` · ${usage.trim()}` : ''}
								</span>
							</div>

							<div className="grid gap-4 sm:grid-cols-2">
								<div className="brand-color-picker">
									<HexColorPicker
										color={hex}
										onChange={(value) => commit(normalizeHex(value))}
									/>
								</div>

								<div className="flex flex-col gap-3">
									<div>
										<label
											htmlFor="brand-color-hex"
											className="mb-1.5 block text-xs text-text-3"
										>
											Hex
										</label>
										<div
											className={cn(
												'flex h-9 items-center overflow-hidden rounded-[var(--r-2)] border bg-surface-input',
												draftValid ? 'border-line-2' : 'border-destructive'
											)}
										>
											<span
												className="ml-1.5 mr-1 h-5 w-5 shrink-0 rounded-[var(--r-1)] border border-line-2"
												style={{ backgroundColor: hex }}
											/>
											<span className="pl-1 font-mono text-sm text-text-4">
												#
											</span>
											<input
												id="brand-color-hex"
												value={draft}
												onChange={(e) => onDraftChange(e.target.value)}
												onBlur={() => {
													const parsed = parseColor(draft);
													if (parsed) commit(parsed);
												}}
												onPaste={(e) => {
													const parsed = parseColor(
														e.clipboardData.getData('text')
													);
													if (parsed) {
														e.preventDefault();
														commit(parsed);
													}
												}}
												maxLength={24}
												spellCheck={false}
												autoComplete="off"
												className="h-full min-w-0 flex-1 border-none bg-transparent pl-0.5 pr-2 font-mono text-sm uppercase text-text-1 outline-none"
											/>
										</div>
										<p
											className={cn(
												'mt-1.5 text-xs',
												draftValid ? 'text-text-4' : 'text-destructive'
											)}
										>
											{draftValid
												? 'Paste #RRGGBB, #RGB or rgb()'
												: 'Not a valid hex color'}
										</p>
									</div>

									<div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 rounded-[var(--r-2)] border border-line-1 bg-surface-sunken px-3 py-2.5 text-xs">
										<span className="text-text-4">RGB</span>
										<span className="font-mono text-text-2">{`${r} · ${g} · ${b}`}</span>
										<span className="text-text-4">CMYK</span>
										<span className="font-mono text-text-2">
											{cmyk.join(' · ')}{' '}
											<span className="font-sans text-2xs text-text-4">
												approx.
											</span>
										</span>
									</div>
								</div>
							</div>

							<div className="grid gap-2 sm:grid-cols-2">
								<ContrastReadout label="On white" ratio={onWhite} />
								<ContrastReadout label="On black" ratio={onBlack} />
							</div>

							{duplicate && (
								<div className="flex items-center gap-2 rounded-[var(--r-2)] bg-status-warn-soft px-3 py-2 text-xs text-text-2">
									<Info size={13} className="shrink-0 text-status-warn" />
									Same hex as{' '}
									<strong className="font-medium">{duplicate.name}</strong> —
									already in this palette.
								</div>
							)}

							<div className="grid gap-4 sm:grid-cols-2">
								<Field label="Color name" required error={nameError}>
									<Input
										value={name}
										onChange={(e) => setName(e.target.value)}
										onBlur={() => setTouched(true)}
										placeholder="e.g. Fjord Green"
									/>
								</Field>

								{/* A peer of the fields around it, not an action:
								    a colour's print spec is filled in or left
								    blank, exactly like its usage. */}
								<Field label="Pantone">
									<PantoneField
										hex={hexChosen ? hex : null}
										value={pantone}
										onChange={(next, picked) => {
											setPantone(next);
											/* The swatch defines the colour when
											   nothing else has: no hex was chosen,
											   so the book's own screen value
											   becomes it. An explicitly picked hex
											   is never overwritten — the two are
											   allowed to differ, and usually do. */
											if (picked && !hexChosen) {
												commit(normalizeHex(picked.hex), false);
											}
										}}
									/>
								</Field>
							</div>

							<Field label="Usage">
								<Textarea
									value={usage}
									onChange={(e) => setUsage(e.target.value)}
									rows={2}
									placeholder="What this colour is for — e.g. packaging and the mark, never body copy"
								/>
							</Field>
						</div>
					</DialogBody>

					<DialogFooter>
						{/* Remove sits apart from the save pair, on the left, so the
						    destructive control is never where Save was a moment ago. */}
						<div>
							{editing && onRemove && (
								<Button
									type="button"
									variant="ghost"
									onClick={onRemove}
									disabled={loading}
								>
									<Trash2 size={14} data-icon="inline-start" />
									Remove
								</Button>
							)}
						</div>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!canSave || loading}>
								{loading ? 'Saving…' : editing ? 'Save color' : 'Add color'}
							</Button>
						</div>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}

function ContrastReadout({ label, ratio }: { label: string; ratio: number }) {
	const grade = contrastGrade(ratio);
	return (
		<div className="flex min-w-0 items-center gap-2 rounded-[var(--r-2)] border border-line-1 bg-surface-sunken px-3 py-2">
			<span className="shrink-0 text-xs text-text-3">{label}</span>
			<span className="font-mono text-xs text-text-1">{ratio.toFixed(1)}:1</span>
			<span
				className={cn(
					'ml-auto shrink-0 text-2xs font-medium',
					grade === 'Fail'
						? 'text-status-danger'
						: grade === 'AA large'
							? 'text-status-warn'
							: 'text-status-success'
				)}
			>
				{grade}
			</span>
		</div>
	);
}
